// #region Imports
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import { debounce, styled } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import axios from "axios";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualByIdQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import MessagingAPIService from "messaging/_api/messaging-api-service";
import Forum from "messaging/forums/forum";
import Topic from "messaging/forums/topics/topic";
import ForumEntityClass from "notifications/values/forum-entity-class/forum-entity-class";
import ForumName from "notifications/values/forum-name/forum-name";
import { enqueueSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import UserInformation from "users/information/user-information";
import { useGetUserNetworkConnectionsQuery } from "users/networking/connections/_api/user-network-connection-api";
// #endregion

// #region Styled Components
const ForumContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  margin: theme.spacing(2, 0),
  width: "100%",
}));
const AddSubscriberContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    paddingLeft: 0,
  },
  minWidth: "15rem",
  paddingLeft: theme.spacing(4),
  textAlign: "left",
}));
const ForumNameCreate = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    width: "100%",
    "&:first-of-type": {
      marginBottom: theme.spacing(1.5),
      marginRight: 0,
    },
  },
  minWidth: theme.spacing(35),
}));
const LoaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  padding: theme.spacing(4),
  paddingTop: 0,
  width: "100%",
}));
const SearchField = styled(TextField)(() => ({
  width: "100%",
})) as typeof TextField;
const ConnectionListContainer = styled(List)(({ theme }) => ({
  maxHeight: "20rem",
  overflowY: "auto",
  padding: theme.spacing(1, 0),
}));
const SelectedIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr 1fr",
  width: "100%",
}));
// #endregion

// #region Types & Props
type CreateForumDialogProps = {
  onForumCreated: (createdForum: Forum) => void;
  onCancel: () => void;
};

export default function CreateForumDialog(props: Readonly<CreateForumDialogProps>) {
  const { onForumCreated, onCancel } = props;

  // #region State
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [forumNameValue, setForumNameValue] = useState<string>("");
  const [subscriberUserIds, setSubscriberUserIds] = useState<string[]>([]);
  const [erroredUserIds, setErroredUserIds] = useState<string[]>([]);

  // #region Hooks
  const session = useSession();

  // #region Queries
  const getIndividualQuery = useGetIndividualByIdQuery(
    { individualId: session.user?.individualId?.value ?? "" }, 
    { skip: !session.user?.individualId });
  const networkConnectionQuery = useGetUserNetworkConnectionsQuery();

  const forumCreator = useMemo(() => {
    if (!getIndividualQuery.data) return null;
    return Individual.fromJSON(getIndividualQuery.data);
  }, [getIndividualQuery.data]);

  const networkConnections = useMemo(() => {
    if (!networkConnectionQuery.data) return [];
    return UserInformation.fromJSONArray(networkConnectionQuery.data);
  }, [networkConnectionQuery.data]);

  // #region Functions
  function search(value: string) {
    setSearchTerm(value);
    if (value === "") return;
    debouncedSearch(value);
  }

  async function saveChanges(): Promise<void> {
    if (!forumCreator) return;

    setIsSaving(true);
    try {
      const subscribers: EntityMember[] = [];
      let missingIndividualIds: string[] = [];

      for (const userId of subscriberUserIds) {
        const individual = networkConnections.find((connection) => connection.id?.value === userId)?.toIndividual();
        if (!individual?.id || !individual.legalEntityId) {
          missingIndividualIds.push(userId);
          continue;
        }
        subscribers.push(
          EntityMember.create({ 
            userId: individual.userId, 
            legalEntityId: individual.legalEntityId 
          })
        );
      }

      if (missingIndividualIds.length > 0) {
        setErroredUserIds(missingIndividualIds);
        console.warn(`Unable to find information for userIds: ${missingIndividualIds.join(", ")}`);
        enqueueSnackbar(
          "Unable to find information for some selected subscribers. Please remove them and try again.", 
          { variant: "warning" }
        );
        return;
      }

      const newForum = Forum.create({
        name: new ForumName(forumNameValue),
        subscribers: subscribers,
        createdDate: new AHDate(),
        creator: EntityMember.create({
          userId: forumCreator.userId,
          legalEntityId: forumCreator.legalEntityId,
        }),
        numUnreadMessages: 0,
        topic: Topic.create({
          entityClass: new ForumEntityClass("userCreatedChat"),
          context: "UserCreatedChat"
        })
      });
      const messagingService = new MessagingAPIService(session);
      const createdForum = await messagingService.createForum(newForum);
      onForumCreated(createdForum);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        enqueueSnackbar("Unable to create Forum: " + error.response.data, { variant: "warning" });
      } else {
        console.error(error);
        enqueueSnackbar("Unable to create Forum", { variant: "error" });
      }
    } finally {
      setIsSaving(false);
    }
  }

  function handleSubscriberSelected(
    _event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    subscriberUserId: string
  ): void {
    const selectedSubscriberIds = [...subscriberUserIds];
    const index = selectedSubscriberIds.indexOf(subscriberUserId);
    if (index === -1) {
      selectedSubscriberIds.push(subscriberUserId);
    } else {
      selectedSubscriberIds.splice(index, 1);
    }
    setSubscriberUserIds(selectedSubscriberIds);
  }

  function isSelected(subscriberUserId: string): boolean {
    return subscriberUserIds.includes(subscriberUserId);
  }

  // #region Variables
  const saveDisabled: boolean = subscriberUserIds.length < 1 || !networkConnections.length || isSaving || !forumNameValue || !forumCreator;
  const debouncedSearch = debounce((_value) => {}, 100);
  const canCreateForum: boolean = networkConnections.length > 0;

  // #region Render
  return (
    <React.Fragment>
      {networkConnectionQuery.isLoading && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      {!networkConnectionQuery.isLoading && !canCreateForum && (
        <LoaderContainer>
          <Typography>You are not networked with anyone</Typography>
        </LoaderContainer>
      )}
      {!networkConnectionQuery.isLoading && canCreateForum && (
        <>
          <ForumContainer>
            <ForumNameCreate
              label="Conversation name"
              required={true}
              helperText="required"
              onChange={(e) => {
                setForumNameValue(e.target.value);
              }}
              value={forumNameValue}
            />
            <AddSubscriberContainer>
              <SearchField
                size="small"
                variant="standard"
                placeholder="Search Connections"
                slotProps={{
                  input: {
                    startAdornment: <SearchIcon sx={{ mr: 1 }} color="disabled" />,
                    endAdornment: (
                      <>
                        {searchTerm.length > 0 && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchTerm("");
                              search("");
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        )}
                      </>
                    ),
                  },
                }}
                value={searchTerm}
                onChange={(event) => search(event.target.value)}
              />
              <ConnectionListContainer dense>
                {networkConnections.map((connection: UserInformation) => {
                  const hasError = erroredUserIds.includes(connection.id?.value ?? "");
                  return (
                    <ListItemButton
                      disableGutters
                      key={connection.id?.value}
                      selected={isSelected(connection.id?.value)}
                      onClick={(event) => handleSubscriberSelected(event, connection.id.value)}
                    >
                      <ListItemAvatar>
                        {isSelected(connection.id?.value) ? (
                          <SelectedIcon>
                            <DoneIcon />
                          </SelectedIcon>
                        ) : (
                          <IndividualAvatar individual={connection.toIndividual()} />
                        )}
                      </ListItemAvatar>
                      <ListItemText 
                        primary={
                          hasError ? `${connection.name.fullName} (Error Loading)` : connection.name.fullName
                        }
                        secondary={connection.email?.value ?? ""}
                        slotProps={{
                          primary: {
                            color: hasError ? "error" : "textPrimary"
                          }
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </ConnectionListContainer>
            </AddSubscriberContainer>
          </ForumContainer>
          <ButtonContainer>
            <LoadingButton
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              loading={isSaving}
              disabled={saveDisabled}
              onClick={saveChanges}
            >
              Save
            </LoadingButton>
            <Button variant="contained" color="primary" onClick={onCancel}>
              Cancel
            </Button>
          </ButtonContainer>
        </>
      )}
    </React.Fragment>
  );
}
